import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import PageBackground from '../components/PageBackground';
import { Trophy, Users, ShoppingCart, Target, Calendar, AlertTriangle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  0: Trophy, 1: Users, 2: ShoppingCart, 3: Target, 4: Calendar, 5: AlertTriangle
};
const COLOR_MAP = {
  0: "text-primary", 1: "text-secondary", 2: "text-yellow-400", 3: "text-green-400", 4: "text-purple-400", 5: "text-red-400"
};

const DEFAULT_SECTIONS = [
  { title: "Overview", items: ["MSF Racing Fantasy is a team management game based on real motorsport results.", "Two leagues: MSF Racing (cars) and MotoMSF (bikes).", "Build your team, apply boosts, and compete on the leaderboard.", "Points are awarded after each race round based on real performance."] },
  { title: "Team Selection", items: ["MSF Racing: Select 2 Drivers + 3 Cars + 1 Tuner within a 200,000 credit budget.", "MotoMSF: Select 2 Riders + 3 Bikes + 1 Tuner within a 150,000 credit budget.", "Each entity has a price. Balance your team to stay within budget.", "Your first team selection is free. Each subsequent change costs a 50 point penalty.", "Team changes are locked during race weekends (selection window closed)."] },
  { title: "Workshop Boosts", items: ["Purchase up to 2 boosts per league from your team budget.", "Apply boosts to any entity in your team for bonus effects.", "Boosts are refunded and reset after each race round.", "Re-purchase your boosts before each round starts!"] },
  { title: "Points & Scoring", items: ["Points are assigned by the admin after each race based on real results.", "Your total score = sum of all your entities' points + boost effects.", "Changing your team after the first selection incurs a 50 point penalty per change."] },
  { title: "Race Calendar & Timing", items: ["The selection window opens before each round for team changes.", "When the window closes, teams are locked for the race weekend.", "Boosts are reset after every round — you must re-purchase before the next round."] },
  { title: "Penalties & Fair Play", items: ["Changing your team outside the initial selection costs 50 points per change.", "Budget includes both team cost and boost cost — you can't overspend.", "All scoring is transparent. Play fair and have fun!"] }
];

export default function Rules({ user }) {
  const [sections, setSections] = useState(DEFAULT_SECTIONS);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/rules-content`).then(res => {
      if (res.data?.rules_sections?.length) setSections(res.data.rules_sections);
    }).catch(() => {});
  }, []);

  return (
    <PageBackground pageKey="rules">
    <div className="min-h-screen">
      <Navbar user={user} />
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="font-heading font-black text-5xl mb-2" data-testid="rules-title">
          GAME <span className="text-primary">RULES</span>
        </h1>
        <p className="text-zinc-400 mb-10">Everything you need to know about MSF Racing Fantasy</p>

        <div className="space-y-8">
          {sections.map((section, idx) => {
            const Icon = ICON_MAP[idx] || Trophy;
            const color = COLOR_MAP[idx] || "text-primary";
            return (
              <div key={section.title || idx} className="bg-card border border-zinc-800 overflow-hidden" data-testid={`rules-section-${idx}`}>
                <div className="flex items-center gap-3 p-5 border-b border-zinc-800">
                  <Icon size={22} className={color} />
                  <h2 className="font-heading font-bold text-xl">{section.title}</h2>
                </div>
                <div className="p-5">
                  <ul className="space-y-3">
                    {section.items.map((item, i) => (
                      <li key={`${section.title || idx}-${item}`} className="flex items-start gap-2 text-sm text-zinc-300">
                        <ArrowRight size={14} className={`mt-0.5 shrink-0 ${color}`} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
    </PageBackground>
  );
}
